import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { 
  ShieldCheck, 
  Globe, 
  CheckCircle2,
  ArrowRight,
  Target,
  Award,
  Users,
  Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const CORPORATE_IMAGE = "https://customer-assets.emergentagent.com/job_compliance-hub-324/artifacts/oxs0ble3_about%20page.png";
const DUBAI_IMAGE = "https://images.unsplash.com/photo-1726533765275-a69cfd7f9897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMHNreWxpbmUlMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzcwNjcwNzMwfDA&ixlib=rb-4.1.0&q=85";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: [0.25, 1, 0.5, 1] }
  }
};

export default function About() {
  const { t, language } = useLanguage();

  const stats = [
    { icon: Globe, value: '3+', label: language === 'en' ? 'Continents' : 'قارات' },
    { icon: Building2, value: '50+', label: language === 'en' ? 'Projects' : 'مشاريع' },
    { icon: Users, value: '100+', label: language === 'en' ? 'Clients' : 'عملاء' },
    { icon: Award, value: '15+', label: language === 'en' ? 'Years Experience' : 'سنوات خبرة' }
  ];

  return (
    <div className="min-h-screen" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden" data-testid="about-hero">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 20px,
              white 20px,
              white 21px
            )`
          }} />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground uppercase tracking-tight mb-6">
              {t.about.title}
            </h1>
            <p className="text-xl text-primary-foreground/80 max-w-3xl mx-auto">
              {t.about.subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-muted border-b border-border" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="text-center"
                  data-testid={`stat-${index}`}
                >
                  <div className="w-16 h-16 mx-auto mb-4 bg-secondary/10 flex items-center justify-center">
                    <Icon className="w-8 h-8 text-secondary" />
                  </div>
                  <div className="text-4xl font-bold text-primary mb-2">{stat.value}</div>
                  <div className="text-muted-foreground font-medium uppercase tracking-wide text-sm">
                    {stat.label}
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 bg-background" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-secondary/20 blur-3xl" />
                <img 
                  src={CORPORATE_IMAGE}
                  alt="Corporate Meeting"
                  className="relative w-full h-[500px] object-cover shadow-2xl"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% 90%, 90% 100%, 0 100%)' }}
                />
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/10 mb-6">
                <Target className="w-5 h-5 text-secondary" />
                <span className="font-semibold text-sm uppercase tracking-wider text-secondary">
                  {t.about.mission}
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-primary uppercase tracking-tight mb-6">
                {language === 'en' ? 'Our Commitment to Excellence' : 'التزامنا بالتميز'}
              </h2>

              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {t.about.missionText}
              </p>

              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-3 px-4 py-2 bg-primary/5 border border-primary/20">
                  <ShieldCheck className="w-5 h-5 text-secondary" />
                  <span className="font-medium text-primary">ISO 45001</span>
                </div>
                <div className="flex items-center gap-3 px-4 py-2 bg-primary/5 border border-primary/20">
                  <ShieldCheck className="w-5 h-5 text-secondary" />
                  <span className="font-medium text-primary">ISO 14001</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why SafePro Section */}
      <section className="py-24 bg-muted" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-5xl font-bold text-primary uppercase tracking-tight mb-4">
              {t.about.whyUs}
            </h2>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {t.about.reasons.map((reason, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card 
                  className="h-full rounded-none border-border hover:border-secondary transition-all duration-300 hover:shadow-lg"
                  data-testid={`reason-${index}`}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-secondary/10 flex items-center justify-center flex-shrink-0">
                        <CheckCircle2 className="w-5 h-5 text-secondary" />
                      </div>
                      <p className="text-foreground leading-relaxed">{reason}</p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Global Presence Section */}
      <section className="py-24 bg-background" data-testid="global-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 mb-6">
                <Globe className="w-5 h-5 text-primary" />
                <span className="font-semibold text-sm uppercase tracking-wider text-primary">
                  {t.about.globalPresence}
                </span>
              </div>

              <h2 className="text-3xl md:text-4xl font-bold text-primary uppercase tracking-tight mb-6">
                {language === 'en' ? 'Operating Worldwide' : 'نعمل في جميع أنحاء العالم'}
              </h2>

              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {t.about.globalText}
              </p>

              <div className="grid grid-cols-3 gap-4">
                {[
                  { region: language === 'en' ? 'Middle East' : 'الشرق الأوسط', flag: '🇦🇪' },
                  { region: language === 'en' ? 'Europe' : 'أوروبا', flag: '🇪🇺' },
                  { region: language === 'en' ? 'Global' : 'عالمياً', flag: '🌍' }
                ].map((item, index) => (
                  <div 
                    key={index}
                    className="text-center p-4 bg-muted border border-border"
                  >
                    <div className="text-3xl mb-2">{item.flag}</div>
                    <div className="text-sm font-semibold text-primary uppercase tracking-wide">
                      {item.region}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-primary/10 blur-3xl" />
                <img 
                  src={DUBAI_IMAGE}
                  alt="Dubai Skyline"
                  className="relative w-full h-[500px] object-cover shadow-2xl"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 10% 100%, 0 90%)' }}
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary" data-testid="about-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-primary-foreground uppercase tracking-tight mb-6">
              {t.cta.title}
            </h2>
            <p className="text-xl text-primary-foreground/80 mb-10 max-w-2xl mx-auto">
              {t.cta.subtitle}
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-none px-10 py-6 text-lg font-semibold uppercase tracking-wide"
                data-testid="about-cta-button"
              >
                {t.cta.button}
                <ArrowRight className="ms-2 w-6 h-6" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
